import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import { Panel, PanelHeader } from '../ui/Panel';
import Pill from '../ui/Pill';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import { LoadingPanel, ErrorNotice, EmptyState } from '../ui/States';
import { fmt } from '../../lib/format';
import { dateLong, orDash } from '../../lib/format';
import { canMutate } from '../../lib/roles';
import { useAuth } from '../../contexts/AuthContext';

interface Side {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string | null;
  hasIbm: boolean;
  ibmLastLoginAt: string | null;
  hasMoodle: boolean;
  isActive: boolean;
  archived: boolean;
  createdAt: string;
  registeredAt: string | null;
  organization: string | null;
  region: string | null;
}

interface Candidate {
  id: string;
  status: 'open' | 'merged' | 'not_same';
  reasons: string[];
  score: number;
  detectedAt: string;
  decidedAt: string | null;
  keptUserId: string | null;
  provisioningHeldFor: string | null;
  suggestedKeepId: string | null;
  a: Side | null;
  b: Side | null;
}

type Status = 'open' | 'not_same' | 'merged';

const STATUS_LABEL: Record<Status, string> = {
  open: 'To review',
  not_same: 'Not the same person',
  merged: 'Merged',
};

const REASON_LABEL: Record<string, string> = {
  email: 'Same email',
  name: 'Same name',
  phone: 'Same phone',
  organization: 'Same organization',
  region: 'Same region',
};

function errorMessage(err: unknown, fallback: string) {
  const data = (err as { response?: { data?: { error?: string; data?: { error?: string } } } })?.response?.data;
  return data?.error ?? data?.data?.error ?? (err instanceof Error ? err.message : fallback);
}

function SideCard({
  side,
  keep,
  held,
  selectable,
  onSelect,
}: {
  side: Side | null;
  keep: boolean;
  held: boolean;
  selectable: boolean;
  onSelect: () => void;
}) {
  if (!side) {
    return <div className="flex-1 px-5 py-4 text-table text-ink-2">Record no longer exists (merged away)</div>;
  }
  const rows: [string, string][] = [
    ['Email', side.email],
    ['Phone', orDash(side.phoneNumber)],
    ['Organization', orDash(side.organization)],
    ['Region', orDash(side.region)],
    ['Registered', side.registeredAt ? dateLong(side.registeredAt) : `record created ${dateLong(side.createdAt)}`],
    [
      'IBM login',
      side.hasIbm
        ? side.ibmLastLoginAt
          ? `last sign-in ${dateLong(side.ibmLastLoginAt)}`
          : 'never signed in'
        : held
          ? 'none — onboarding held for this review'
          : 'none',
    ],
    ['Moodle', side.hasMoodle ? 'linked' : 'not linked'],
  ];
  return (
    <div className={`flex-1 min-w-0 px-5 py-4 ${keep ? 'bg-selected' : ''}`}>
      <div className="flex items-center justify-between gap-3">
        <Link to={`/analytics/members/${side.id}`} className="text-table font-semibold link-underline truncate">
          {side.firstName} {side.lastName}
        </Link>
        <div className="flex items-center gap-2 shrink-0">
          {side.archived ? <Pill tone="inactive">Archived</Pill> : null}
          {keep ? <Pill tone="active">Kept on merge</Pill> : null}
        </div>
      </div>
      <dl className="mt-3 grid gap-y-[6px] text-small" style={{ gridTemplateColumns: '110px 1fr' }}>
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-ink-2">{k}</dt>
            <dd className="truncate">{v}</dd>
          </div>
        ))}
      </dl>
      {selectable && !keep ? (
        <button type="button" onClick={onSelect} className="mt-3 text-small text-ink-2 link-underline">
          Keep this record instead
        </button>
      ) : null}
    </div>
  );
}

function CandidateCard({ c, mutable, onDone }: { c: Candidate; mutable: boolean; onDone: () => void }) {
  const [keepId, setKeepId] = useState<string | null>(c.suggestedKeepId);
  const [confirm, setConfirm] = useState(false);
  const open = c.status === 'open';
  const keep = open ? keepId : c.keptUserId;
  const other = keep === c.a?.id ? c.b : c.a;
  const kept = keep === c.a?.id ? c.a : c.b;

  const merge = useMutation(
    async () => {
      const data = (await api.post(`/duplicates/${c.id}/merge`, { keepId })).data.data;
      if (!data.ok) throw new Error(data.error);
      return data;
    },
    {
      onSuccess: () => {
        setConfirm(false);
        onDone();
      },
    },
  );
  const notSame = useMutation(async () => (await api.post(`/duplicates/${c.id}/not-same`)).data.data, {
    onSuccess: onDone,
  });

  return (
    <Panel className="mb-5">
      <PanelHeader
        title={
          <span className="flex items-center gap-2 flex-wrap">
            {c.reasons.map((r) => (
              <Pill key={r} tone={['email', 'name', 'phone'].includes(r) ? 'running' : 'planned'}>
                {REASON_LABEL[r] ?? r}
              </Pill>
            ))}
          </span>
        }
        note={`Found ${dateLong(c.detectedAt)}${c.decidedAt ? ` · decided ${dateLong(c.decidedAt)}` : ''}`}
      />
      <div className="flex divide-x divide-line">
        <SideCard
          side={c.a}
          keep={Boolean(c.a && keep === c.a.id && c.status !== 'not_same')}
          held={c.provisioningHeldFor === c.a?.id}
          selectable={open && mutable}
          onSelect={() => c.a && setKeepId(c.a.id)}
        />
        <SideCard
          side={c.b}
          keep={Boolean(c.b && keep === c.b.id && c.status !== 'not_same')}
          held={c.provisioningHeldFor === c.b?.id}
          selectable={open && mutable}
          onSelect={() => c.b && setKeepId(c.b.id)}
        />
      </div>
      {open ? (
        <div className="flex items-center gap-3 px-5 py-4 border-t border-line">
          <Button size="sm" variant="dark" disabled={!mutable || !keepId} onClick={() => setConfirm(true)}>
            Merge
          </Button>
          <Button size="sm" variant="outline" disabled={!mutable || notSame.isLoading} onClick={() => notSame.mutate()}>
            {notSame.isLoading ? 'Saving…' : 'Not the same person'}
          </Button>
          {c.provisioningHeldFor ? (
            <span className="text-small text-ink-2">Not the same person also sends the held welcome email.</span>
          ) : null}
          {notSame.isError ? (
            <ErrorNotice>{errorMessage(notSame.error, 'Could not record the decision.')}</ErrorNotice>
          ) : null}
        </div>
      ) : null}

      {confirm && kept && other ? (
        <Modal
          title="Merge these records"
          onClose={() => setConfirm(false)}
          footer={
            <>
              <Button variant="destructive" disabled={merge.isLoading} onClick={() => merge.mutate()}>
                {merge.isLoading ? 'Merging…' : 'Merge'}
              </Button>
              <Button variant="ghost" onClick={() => setConfirm(false)}>
                Cancel
              </Button>
            </>
          }
        >
          <p className="text-table">
            Keeps <strong>{kept.email}</strong> and merges <strong>{other.email}</strong> into it:
          </p>
          <ul className="list-disc pl-5 mt-2 text-small text-ink-2 space-y-1">
            <li>its mentorship and Moodle accounts move to {kept.email}, where it has them</li>
            <li>
              {other.hasIbm && kept.hasIbm
                ? `its IBM login (${other.email}) is deleted — it can no longer sign in with that address`
                : other.hasIbm
                  ? `the kept record takes over its IBM login (${other.email})`
                  : 'it has no IBM login to delete'}
            </li>
            <li>
              its registrations, attendance, courses and history move to the kept record, and the newer profile survives
            </li>
          </ul>
          <p className="text-small mt-3">This cannot be undone.</p>
          {merge.isError ? (
            <div className="mt-3">
              <ErrorNotice>
                <div>{errorMessage(merge.error, 'The merge did not go through.')}</div>
                <div className="mt-1">
                  Any step that completed is listed in the kept member’s activity log on Member 360.
                </div>
              </ErrorNotice>
            </div>
          ) : null}
        </Modal>
      ) : null}
    </Panel>
  );
}

/** Possible duplicate people, at the top of Members: open when pairs wait for a decision, one folded line otherwise. */
export default function PossibleDuplicates() {
  const { user } = useAuth();
  const mutable = canMutate(user?.role);
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<Status>('open');
  const [shown, setShown] = useState<boolean | null>(null);

  const waiting = useQuery<Candidate[]>(
    ['duplicates', 'open'],
    async () => (await api.get('/duplicates', { params: { status: 'open' } })).data.data,
  );
  const list = useQuery<Candidate[]>(
    ['duplicates', status],
    async () => (await api.get('/duplicates', { params: { status } })).data.data,
    { enabled: status !== 'open' },
  );
  const rows = status === 'open' ? waiting : list;
  const refresh = () => {
    queryClient.invalidateQueries('duplicates');
    queryClient.invalidateQueries('dashboard');
  };
  const detect = useMutation(async () => (await api.post('/duplicates/detect')).data.data, { onSuccess: refresh });

  if (waiting.isLoading) return null;
  const count = waiting.data?.length ?? 0;
  const expanded = shown ?? count > 0;

  return (
    <section className={expanded ? 'mb-6' : 'mb-2'} aria-label="Possible duplicates">
      <div
        className={`flex flex-wrap items-center justify-between gap-x-6 gap-y-2 ${expanded ? 'border-b border-line-strong pb-2 mb-4' : ''}`}
      >
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setShown(!expanded)}
          className="flex items-center gap-2 min-h-[44px] text-left"
        >
          <svg
            aria-hidden="true"
            width="14"
            height="14"
            viewBox="0 0 16 16"
            className={`text-ink-3 self-center transition-transform ${expanded ? 'rotate-90' : ''}`}
          >
            <path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <span className="font-serif font-semibold text-heading text-ink">Possible duplicates</span>
          <span className="text-[15px] text-ink-3">· {count ? `${fmt(count)} to review` : 'none to review'}</span>
        </button>
        {expanded ? (
          <Button size="sm" variant="outline" disabled={!mutable || detect.isLoading} onClick={() => detect.mutate()}>
            {detect.isLoading ? 'Checking…' : 'Check all members now'}
          </Button>
        ) : null}
      </div>
      {expanded ? (
        <>
          <p className="text-small text-ink-2 mb-4 max-w-[72ch]">
            Members who may have registered twice. Found on every registration and nightly; a pair marked not the same
            person never comes back.
          </p>
          <div className="flex flex-wrap items-center gap-2 mb-5">
            {(Object.keys(STATUS_LABEL) as Status[]).map((s) => (
              <Button key={s} size="sm" variant={s === status ? 'dark' : 'ghost'} onClick={() => setStatus(s)}>
                {STATUS_LABEL[s]}
              </Button>
            ))}
            {detect.isSuccess ? (
              <span className="text-small text-ink-2 ml-3">
                {detect.data.found} pairs found, {detect.data.created} new
              </span>
            ) : null}
          </div>
          {detect.isError ? <ErrorNotice>{errorMessage(detect.error, 'Detection failed.')}</ErrorNotice> : null}
          {rows.isError ? <ErrorNotice>Could not load possible duplicates.</ErrorNotice> : null}
          {rows.isLoading ? <LoadingPanel label="Loading possible duplicates…" /> : null}
          {rows.data && rows.data.length === 0 ? (
            <Panel>
              <EmptyState title="Nothing here">
                {status === 'open' ? 'No member records look like the same person.' : 'No decisions of this kind yet.'}
              </EmptyState>
            </Panel>
          ) : null}
          {(rows.data ?? []).map((c) => (
            <CandidateCard key={c.id} c={c} mutable={mutable} onDone={refresh} />
          ))}
        </>
      ) : null}
    </section>
  );
}
